import { useEffect, useState, type ReactNode } from 'react';
import { Section, SectionHeading } from '~/components/layout';
import { useContent } from '~/lib/useLanguage';
import type { DisplayCurrency } from '~/content/ui';
import { CurrencyFlag, type CurrencyCode } from '~/components/ui';
import { cn } from '~/lib/cn';
import { currencyFlagSrc } from '~/lib/currency-flag';
import { getComparison, type Competitor } from '~/lib/compare';
import {
  FALLBACK_RATES,
  RECEIVE_CURRENCIES,
  getQuote,
  type PricingTier,
  type Quote,
  type ReceiveCurrency,
} from '~/lib/quote';

/*
 * Reusable rate comparison table (column-per-provider, Figma Compare page).
 *
 * Competitor columns show REAL, live data. The nsave backend has no
 * multi-provider comparison endpoint, so — mirroring what arrow-dev's
 * `WiseFxProvider` does server-side — the competitor rates/fees/markups come
 * from Wise's public comparison API via the `getComparison` server function
 * (see `~/lib/compare`). Providers, their rates, fees, hidden markups and payout
 * amounts are whatever Wise reports for the corridor + amount (e.g. Wise,
 * Remitly, Xoom, WorldRemit …), rendered with their real brand logos.
 *
 * Two modes:
 *  - Uncontrolled (compare page): renders its OWN Amount / From / To selectors
 *    over GBP/USD/EUR. The nsave column has no public quote for hard-currency
 *    pairs, so it shows the real mid-market rate (best live provider rate) with
 *    nsave's zero markup/fee.
 *  - Controlled + visible selectors (send-money-home): pass `amount`/`source`/
 *    `target`/`plan` AND the `on*Change` handlers. The Amount/From/To selectors
 *    are then shown but bound to the SHARED state that also drives the calculator
 *    and the rates strip (two-way sync — editing either surface updates the
 *    other). The nsave column reflects the SAME live quote/plan the calculator
 *    shows (reuse `getQuote`); source is USD, target is a supported payout
 *    currency.
 *
 * If the live comparison is unreachable, the competitor columns gracefully fall
 * back to a clearly-marked demo set so the table never shows a broken state.
 */
const CURRENCIES: CurrencyCode[] = ['GBP', 'USD', 'EUR'];

/** Mid-market value of one unit of each currency, expressed in USD. */
const USD_PER: Record<CurrencyCode, number> = {
  USD: 1,
  GBP: 1.27,
  EUR: 1.09,
};

/** Send options for the controlled (send-money-home) selectors — USD only. */
const SEND_CURRENCIES: CurrencyCode[] = ['USD'];

/**
 * Selector options carry the code and its display name, and the name is
 * language-dependent, so the list is built per render from the active bundle
 * rather than frozen at module scope in English.
 */
function currencyOptions(
  codes: readonly DisplayCurrency[],
  names: Record<DisplayCurrency, string>,
): { code: string; name: string }[] {
  return codes.map((code) => ({ code, name: names[code] }));
}

/** Fiat flags (GBP/USD/EUR) use the inline SVG badge; payout flags use PNGs. */
function renderFiatFlag(code: string): ReactNode {
  return <CurrencyFlag code={code as CurrencyCode} className="size-8" />;
}
function renderPayoutFlag(code: string): ReactNode {
  return (
    <img
      src={currencyFlagSrc(code)}
      alt=""
      aria-hidden="true"
      className="size-8 rounded-full object-cover"
    />
  );
}

/**
 * Real brand logos for competitors returned by the Wise comparison API, copied
 * from Wise's CDN into `public/images/providers/`. Keyed by Wise provider alias.
 * Providers without a local asset fall back to a monogram badge.
 */
const PROVIDER_LOGOS: Record<string, string> = {
  wise: '/images/providers/wise.svg',
  remitly: '/images/providers/remitly.svg',
  xoom: '/images/providers/xoom.svg',
  'world-remit': '/images/providers/world-remit.svg',
  instarem: '/images/providers/instarem.svg',
  moneygram: '/images/providers/moneygram.svg',
  ofx: '/images/providers/ofx.svg',
  chase: '/images/providers/chase.svg',
  'bank-of-america': '/images/providers/bank-of-america.svg',
};

/**
 * TODO(launch): demo competitor offsets used ONLY when the live Wise comparison
 * is unreachable, so the table degrades gracefully instead of collapsing to a
 * lone nsave column. Real values come from `getComparison`.
 */
const DEMO_COMPETITORS: {
  alias: string;
  name: string;
  transferFeePct: number;
  markupPct: number;
}[] = [
  { alias: 'wise', name: 'Wise', transferFeePct: 0.0103, markupPct: 0 },
  { alias: 'paypal', name: 'PayPal', transferFeePct: 0.022, markupPct: 0 },
  { alias: 'revolut', name: 'Revolut', transferFeePct: 0.0284, markupPct: 0 },
];

/** Max competitor columns shown alongside nsave (preserves the 4-column design). */
const MAX_COMPETITORS = 3;

type DisplayProvider = {
  key: string;
  name: string;
  isNsave?: boolean;
  isDemo?: boolean;
  logo?: string | null;
  rate: number;
  transferFee: number;
  markupFee: number;
  totalCost: number;
  recipientGets: number;
};

const money = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const rateFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function crossRate(from: CurrencyCode, to: CurrencyCode) {
  return USD_PER[from] / USD_PER[to];
}

// Selector field styling, matched to the rewards page (RewardsCalculator): an
// outlined box (no fill) with a 20px radius, 72px tall, 1.5px border resting at
// black-6 and turning grey-900 on focus (border-box → no layout shift). The label
// is 17sb with a 20px gap to its field and pl-4 so it aligns with the field value.
const fieldLabelClass = 'mb-5 block pl-4 text-body font-semibold tracking-[-0.01em] text-grey-900';
const fieldBoxBase =
  'h-[72px] rounded-[20px] border-[1.5px] border-black-6 bg-transparent transition-colors';

function ProviderBadge({ provider }: { provider: DisplayProvider }) {
  // nsave now uses a real brand icon (same size/treatment as competitor logos).
  const logo = provider.isNsave ? '/images/providers/nsave.png' : provider.logo;
  if (logo) {
    return (
      <img
        src={logo}
        alt={provider.name}
        width={44}
        height={44}
        className="size-11 rounded-full"
        loading="lazy"
      />
    );
  }
  return (
    <span
      className="grid size-11 place-items-center rounded-full bg-grey-100 text-body font-semibold text-grey-600"
      aria-hidden="true">
      {provider.name.charAt(0)}
    </span>
  );
}

function CurrencySelect({
  label,
  value,
  options,
  renderFlag,
  onChange,
}: {
  label: string;
  value: string;
  options: { code: string; name: string }[];
  renderFlag: (code: string) => ReactNode;
  onChange: (code: string) => void;
}) {
  const current = options.find((c) => c.code === value) ?? options[0];
  return (
    <label className="block">
      <span className={fieldLabelClass}>{label}</span>
      <span
        className={cn(
          fieldBoxBase,
          'relative flex items-center gap-3 px-4 focus-within:border-grey-900',
        )}>
        {renderFlag(current.code)}
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="truncate text-body font-semibold text-ink [text-box-trim:none]">
            {current.code}
          </span>
          <span className="truncate text-body font-medium text-grey-900 [text-box-trim:none]">
            {current.name}
          </span>
        </span>
        <svg
          className="ml-auto size-5 text-grey-500"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true">
          <path
            d="M6 8l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          className="absolute inset-0 w-full cursor-pointer opacity-0">
          {options.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

/** Fallback nsave quote used before the first live quote / if upstream fails. */
function fallbackNsaveQuote(target: ReceiveCurrency, amount: number): Quote {
  const rate = FALLBACK_RATES[target];
  return { rate, fee: 1, amount: Math.max(amount - 1, 0) * rate };
}

export function RateComparison({
  amount: amountProp,
  source: sourceProp,
  target: targetProp,
  plan = 'PRO',
  showSelectors = true,
  showSavings = false,
  heading,
  onAmountChange,
  onSourceChange,
  onTargetChange,
}: {
  amount?: number;
  source?: string;
  target?: string;
  plan?: PricingTier;
  showSelectors?: boolean;
  showSavings?: boolean;
  heading?: string;
  /**
   * When any `on*Change` handler is provided the selectors become CONTROLLED and
   * bound to the parent's shared state (send-money-home two-way sync). When all
   * are absent the component keeps its own internal selector state (compare page).
   */
  onAmountChange?: (value: string) => void;
  onSourceChange?: (code: string) => void;
  onTargetChange?: (code: string) => void;
} = {}) {
  const t = useContent().ui.calculator;
  const [amountState, setAmount] = useState(25000);
  const [fromState, setFrom] = useState<CurrencyCode>('GBP');
  const [toState, setTo] = useState<CurrencyCode>('USD');

  // Controlled when the parent supplies change handlers (send-money-home). The
  // selectors then read/write the shared state instead of the internal state.
  const controlled = Boolean(onAmountChange || onSourceChange || onTargetChange);

  const amount = amountProp ?? amountState;
  const from = sourceProp ?? fromState;
  const to = targetProp ?? toState;

  const handleAmountChange = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    if (onAmountChange) onAmountChange(digits);
    else setAmount(Number(digits) || 0);
  };
  const handleFromChange = (code: string) => {
    if (onSourceChange) onSourceChange(code);
    else setFrom(code as CurrencyCode);
  };
  const handleToChange = (code: string) => {
    if (onTargetChange) onTargetChange(code);
    else setTo(code as CurrencyCode);
  };

  const fromOptions = currencyOptions(controlled ? SEND_CURRENCIES : CURRENCIES, t.currencyNames);
  const toOptions = currencyOptions(controlled ? RECEIVE_CURRENCIES : CURRENCIES, t.currencyNames);
  const renderFlag = controlled ? renderPayoutFlag : renderFiatFlag;

  // The nsave column mirrors the calculator's live quote when the pair is a
  // supported USD payout corridor (send-money-home). getQuote assumes USD source.
  const liveTarget =
    from === 'USD' && (RECEIVE_CURRENCIES as readonly string[]).includes(to)
      ? (to as ReceiveCurrency)
      : null;
  const nsaveKey = liveTarget ? `${liveTarget}:${amount}:${plan}` : null;
  const [nsaveResult, setNsaveResult] = useState<{ key: string; quote: Quote } | null>(null);
  const nsaveLive = nsaveKey && nsaveResult?.key === nsaveKey ? nsaveResult.quote : null;

  // Live competitor comparison (real Wise data). Keyed on pair + amount.
  const compareKey = amount > 0 ? `${from}:${to}:${amount}` : null;
  const [compareResult, setCompareResult] = useState<{
    key: string;
    competitors: Competitor[];
  } | null>(null);
  const liveCompetitors =
    compareKey && compareResult?.key === compareKey ? compareResult.competitors : null;

  // Debounced, race-safe live nsave quote (same render-derived pattern as the
  // calculator). setState only runs inside the async callbacks.
  useEffect(() => {
    if (!nsaveKey || !liveTarget) return;
    const key = nsaveKey;
    const timer = setTimeout(() => {
      void getQuote({ data: { targetCurrency: liveTarget, amount, pricingTier: plan } })
        .then((live) => setNsaveResult({ key, quote: live }))
        .catch(() => setNsaveResult({ key, quote: fallbackNsaveQuote(liveTarget, amount) }));
    }, 300);
    return () => clearTimeout(timer);
  }, [nsaveKey, liveTarget, amount, plan]);

  // Debounced, race-safe live competitor comparison. On failure we store an empty
  // list so the render falls back to the demo competitor set.
  useEffect(() => {
    if (!compareKey) return;
    const key = compareKey;
    const timer = setTimeout(() => {
      void getComparison({ data: { sourceCurrency: from, targetCurrency: to, amount } })
        .then((competitors) => setCompareResult({ key, competitors }))
        .catch(() => setCompareResult({ key, competitors: [] }));
    }, 300);
    return () => clearTimeout(timer);
  }, [compareKey, from, to, amount]);

  // Mid-market reference rate: the live nsave rate when available, else the best
  // live competitor rate (Wise reports mid-market), else a static fallback.
  const midRate =
    nsaveLive?.rate ??
    (liveCompetitors && liveCompetitors.length
      ? Math.max(...liveCompetitors.map((c) => c.rate))
      : liveTarget
        ? FALLBACK_RATES[liveTarget]
        : crossRate(from as CurrencyCode, to as CurrencyCode));

  const nsaveRow: DisplayProvider = nsaveLive
    ? {
        key: 'nsave',
        name: 'nsave',
        isNsave: true,
        rate: nsaveLive.rate,
        transferFee: nsaveLive.fee,
        markupFee: 0,
        totalCost: nsaveLive.fee,
        recipientGets: nsaveLive.amount,
      }
    : {
        key: 'nsave',
        name: 'nsave',
        isNsave: true,
        rate: midRate,
        transferFee: 0,
        markupFee: 0,
        totalCost: 0,
        recipientGets: amount * midRate,
      };

  const competitorRows: DisplayProvider[] =
    liveCompetitors && liveCompetitors.length
      ? liveCompetitors.slice(0, MAX_COMPETITORS).map((c) => ({
          key: c.alias,
          name: c.name,
          logo: PROVIDER_LOGOS[c.alias] ?? null,
          rate: c.rate,
          transferFee: c.fee,
          markupFee: c.markup,
          totalCost: c.fee + c.markup,
          recipientGets: c.receivedAmount,
        }))
      : DEMO_COMPETITORS.map((p) => {
          const transferFee = amount * p.transferFeePct;
          const markupFee = amount * p.markupPct;
          const totalCost = transferFee + markupFee;
          return {
            key: p.alias,
            name: p.name,
            isDemo: true,
            logo: PROVIDER_LOGOS[p.alias] ?? null,
            rate: midRate,
            transferFee,
            markupFee,
            totalCost,
            recipientGets: Math.max(amount - totalCost, 0) * midRate,
          };
        });

  const results: DisplayProvider[] = [nsaveRow, ...competitorRows];

  const worstRecipientGets = results.reduce(
    (min, r) => Math.min(min, r.recipientGets),
    Number.POSITIVE_INFINITY,
  );
  const savings = Math.max(nsaveRow.recipientGets - worstRecipientGets, 0);

  const rows = [
    {
      id: 'youSend',
      label: t.youSend,
      // Same send amount across every provider column (input amount + source).
      render: (_r: DisplayProvider) => `${money.format(amount)} ${from}`,
    },
    {
      id: 'transferFee',
      label: t.transferFee,
      render: (r: DisplayProvider) => `${money.format(r.transferFee)} ${from}`,
    },
    {
      id: 'rate',
      label: t.exchangeRate,
      render: (r: DisplayProvider) => rateFormat.format(r.rate),
    },
    {
      id: 'recipient',
      label: t.recipientGets,
      emphasise: true,
      render: (r: DisplayProvider) => `${money.format(r.recipientGets)} ${to}`,
    },
  ];

  return (
    <Section className="!pt-[104px] md:!pt-[120px]">
      {heading && <SectionHeading title={heading} />}
      {/* 56px gap (mt-14) below the SectionHeading divider so the selectors don't
          crowd the line above them. */}
      {showSelectors && (
        <div className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-3">
          <label className="col-span-2 block md:col-span-1">
            <span className={fieldLabelClass}>{t.amount}</span>
            <span
              className={cn(fieldBoxBase, 'flex items-center px-4 focus-within:border-grey-900')}>
              <input
                inputMode="numeric"
                value={amount ? amount.toLocaleString('en-US') : ''}
                onChange={(e) => handleAmountChange(e.target.value)}
                aria-label={t.amountLabel}
                className="w-full bg-transparent text-body font-medium tabular-nums text-ink outline-none"
              />
            </span>
          </label>
          <CurrencySelect
            label={t.from}
            value={from}
            options={fromOptions}
            renderFlag={renderFlag}
            onChange={handleFromChange}
          />
          <CurrencySelect
            label={t.to}
            value={to}
            options={toOptions}
            renderFlag={renderFlag}
            onChange={handleToChange}
          />
        </div>
      )}

      {competitorRows.some((provider) => provider.isDemo) ? (
        <p className="mt-6 text-body-s text-ink-secondary">{t.demoRatesNotice}</p>
      ) : null}

      {/* table-fixed → every column (the label column + nsave + up to 3
          competitors) is an equal fraction of the width, so the grid adapts to the
          competitor count (5 cols with 3 competitors, 4 with 2, 3 with 1). */}
      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
          <thead>
            <tr>
              <th />
              {results.map((r) => (
                <th
                  key={r.key}
                  className={cn('px-4 py-6 text-center align-middle', r.isNsave && 'bg-grey-25')}>
                  <span className="flex flex-col items-center gap-4">
                    <ProviderBadge provider={r} />
                    <span className="text-body font-semibold text-grey-1000">{r.name}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-black-6">
                <th
                  scope="row"
                  className="h-[116px] px-2 text-left align-middle text-body font-semibold text-ink">
                  {row.label}
                </th>
                {results.map((r) => (
                  <td
                    key={r.key}
                    className={cn(
                      'h-[116px] px-4 text-center align-middle text-body-m',
                      r.isNsave && 'bg-grey-25',
                      row.emphasise
                        ? r.isNsave
                          ? 'font-semibold text-ink'
                          : 'font-medium text-red-500'
                        : 'text-grey-900',
                    )}>
                    {row.render(r)}
                    {row.id === 'recipient' && r.isNsave && showSavings && savings > 0 && (
                      <span className="mt-2 block text-body-s font-medium text-green">
                        {t.saveUpTo} {money.format(savings)} {to}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
